/**
 * Resolve an AI page slug + params into a navigation call. Used by the
 * `client_action` widget's `navigate` action.
 *
 * Every entry in `AIPageParamsMap` MUST have a case here, otherwise the
 * widget renders a "no destination" disabled state for that slug. This
 * is the single source of truth for slug → screen mapping in the app.
 *
 * Report navigations (`report`, `*_report`) need a full `ReportRef`
 * with vehicle metadata that the AI doesn't have on hand, so those
 * cases fetch the report once via the report service to build it.
 */

import { pushChatConversation } from '@euler/app/flows/chat/lib/navigation/openChatConversation';
import { ReportRef } from '@euler/app/flows/report/functions/useReportData';
import { getGlobalNavigationRef } from '@euler/app/navigation/RoutesContainer';
import {
  AIClientActionNavigate,
  AIPageParamsMap,
  AIPageSlug,
} from '@euler/model/ai';
import { ReportService } from '@euler/services/report.service';
import { ServiceRecordService } from '@euler/services/service-record.service';

export type SlugNavigate = ReturnType<typeof getGlobalNavigationRef>;

export type SlugNavigateContext = {
  navigation: SlugNavigate;
  reportService: ReportService;
  serviceRecordService: ServiceRecordService;
};

export async function navigateForSlug(
  ctx: SlugNavigateContext,
  payload: AIClientActionNavigate,
): Promise<void> {
  const { navigation } = ctx;
  switch (payload.page) {
    // ── tab / list screens ──
    case 'task_list':
      navigation.navigate('Main', {
        screen: 'Tabs',
        params: { screen: 'Dashboard' },
      });
      return;
    case 'history_list':
      navigation.navigate('Main', {
        screen: 'Tabs',
        params: { screen: 'History' },
      });
      return;
    case 'inbox':
      navigation.navigate('Main', {
        screen: 'Tabs',
        params: { screen: 'Inbox' },
      });
      return;
    case 'maintenance_plans':
      navigation.navigate('Main', {
        screen: 'Tabs',
        params: { screen: 'Plans' },
      });
      return;
    case 'leads_center':
      navigation.navigate('Main', {
        screen: 'Tabs',
        params: { screen: 'Leads' },
      });
      return;
    case 'reservations_center':
      navigation.navigate('Main', {
        screen: 'Tabs',
        params: { screen: 'Reservations' },
      });
      return;
    case 'profile':
      navigation.navigate('Main', {
        screen: 'Tabs',
        params: { screen: 'Me', params: { screen: '_profile' } },
      });
      return;
    case 'settings':
      navigation.navigate('Main', {
        screen: 'Tabs',
        params: { screen: 'Me', params: { screen: 'settings' } },
      });
      return;

    // ── task-scoped screens (Task flow nested) ──
    case 'task_detail':
      navigateTask(navigation, payload, 'taskDetail');
      return;
    case 'pre_inspection':
      navigateTask(navigation, payload, 'preInspection');
      return;
    case 'delivery_check':
      navigateTask(navigation, payload, '_deliveryCheck');
      return;
    case 'inspection': {
      // Task's typed AppNavParams entry is `{ taskNo }` — its initial
      // route. Deep-linking into a specific TaskFlow inner screen from
      // the root navigator isn't supported by the current type surface,
      // so land on Task's initial screen (taskDetail) with the taskNo
      // and let the user tap into the inspection flow from there.
      // (Same pattern as useNavigateToTaskListItem.)
      navigation.navigate('Task', { taskNo: payload.params.taskNo });
      return;
    }
    case 'construction_job': {
      navigation.navigate('Task', { taskNo: payload.params.taskNo });
      return;
    }
    case 'construction_schedule':
      navigateTask(navigation, payload, '_scheduleConstructionJobs');
      return;
    case 'order_detail':
      navigateTask(navigation, payload, '_orderDetail');
      return;
    case 'pending_issues':
      navigateTask(navigation, payload, '_pendingIssues');
      return;
    case 'quotation':
      navigateTask(navigation, payload, '_quotation');
      return;
    case 'settlement':
      navigateTask(navigation, payload, '_quotation');
      return;
    case 'task_timeline':
      navigateTask(navigation, payload, 'timeline');
      return;

    // ── service history ──
    case 'service_record': {
      const { orderNo, serviceRecordNo } = payload.params;
      const id = serviceRecordNo
        ? serviceRecordNo
        : await resolveServiceRecordIdByOrderNo(ctx, orderNo);
      if (!id) {
        throw new Error(`未找到工单 ${orderNo} 对应的服务记录`);
      }
      navigation.navigate('ServiceRecord', { id });
      return;
    }

    // ── lead / reservation ──
    // Both open on the ROOT stack (`_lead` / `_reservation`), not inside the
    // leads or reservations tab, so back returns to whatever the user was
    // looking at when the assistant offered the link.
    case 'lead_detail':
      navigation.navigate('_lead', { id: payload.params.leadId });
      return;
    case 'reservation_detail':
      navigation.navigate('_reservation', {
        id: payload.params.reservationId,
      });
      return;

    // ── chat ──
    case 'chat_room': {
      const { roomId } = payload.params;
      // A room opens as the root-stack `_chat` flow, carrying the intent as
      // its params — pushed, so it stacks over whatever is on screen.
      pushChatConversation(navigation, { kind: 'room', roomId });
      return;
    }

    // ── BI ──
    case 'bi_dashboard':
      navigation.navigate('BiDashboard', {
        dashboardId: payload.params.dashboardId,
      });
      return;
    case 'bi_card':
      navigation.navigate('BiDashboardCard', {
        dashboardId: payload.params.dashboardId,
        cardId: payload.params.cardId,
      });
      return;
    case 'bi_dataset':
      navigation.navigate('BiDataset', {
        datasetRef: payload.params.datasetId,
      });
      return;

    // ── report screens ──
    case 'report':
      await navigateReport(ctx, payload.params, null);
      return;
    case 'pre_inspection_report':
      await navigateReport(ctx, payload.params, 'preInspectionReport');
      return;
    case 'inspection_report':
      await navigateReport(ctx, payload.params, 'inspectionReport');
      return;
    case 'construction_report':
      await navigateReport(ctx, payload.params, 'constructionReport');
      return;
    case 'delivery_check_report':
      await navigateReport(ctx, payload.params, 'deliveryCheckReport');
      return;
  }
}

type TaskScopedSlug = {
  [K in AIPageSlug]: AIPageParamsMap[K] extends { taskNo: string } ? K : never;
}[AIPageSlug];

function navigateTask<S extends TaskScopedSlug>(
  navigation: SlugNavigate,
  payload: Extract<AIClientActionNavigate, { page: S }>,
  // `screen` unused for now — see `case 'inspection'` above. Kept in
  // the signature so callers signal intent; when Task's typed params
  // grow a nested-navigator shape we can honor it here.
  _screen: string,
): void {
  const taskNo = (payload.params as { taskNo: string }).taskNo;
  navigation.navigate('Task', { taskNo });
}

type ReportScreenName =
  | 'preInspectionReport'
  | 'inspectionReport'
  | 'constructionReport'
  | 'deliveryCheckReport';

async function navigateReport(
  ctx: SlugNavigateContext,
  params: { orderNo: string; reportNo: string },
  screen: ReportScreenName | null,
): Promise<void> {
  const { navigation, reportService } = ctx;
  const target = await buildReportRef(reportService, params);
  if (screen == null) {
    navigation.navigate('Report', { target });
  } else {
    navigation.navigate(screen, { target, standalone: true });
  }
}

/**
 * Fallback path when the AI didn't pass a `serviceRecordNo` — hit the
 * service-records/search endpoint's order_no filter. Returns null
 * when no matching record exists (the AI likely tried to open the
 * service record before the task transitioned into service-record
 * status).
 */
async function resolveServiceRecordIdByOrderNo(
  ctx: SlugNavigateContext,
  orderNo: string,
): Promise<string | null> {
  const rows = await ctx.serviceRecordService.search({ orderNo }, null, 0, 1);
  return rows[0]?._id ?? null;
}

async function buildReportRef(
  reportService: ReportService,
  params: { orderNo: string; reportNo: string },
): Promise<ReportRef> {
  const detail = await reportService.getReportDetail(params.reportNo, {});
  return {
    type: 'report',
    id: params.reportNo,
    orderNo: detail.orderNo ?? params.orderNo,
    reportNo: params.reportNo,
    licensePlateNo: detail.licensePlateNo,
    vehicleName: detail.vehicleName,
    vehicleImageUrl: detail.dashboardImgUrl ?? null,
  };
}
